<script setup>
import { computed, onMounted, ref } from 'vue';
import { UploadButton } from '@lib/pp-shop-lib/components';
import { useConfigStore } from '@lib/pp-shop-lib/stores';
import { psModal } from '@lib/pp-shop-lib/helpers';

defineOptions({
    name: 'HomeView'
});

const configStore = useConfigStore();

const tiles = ref([]);
const loadingTiles = ref(false);
const tileLoadError = ref('');

const contentBaseUrl = computed(() => {
    const raw = configStore.contentBase || configStore.strapiUrl || '';
    return String(raw).trim().replace(/\/+$/, '');
});

const contentTilesPath = computed(() => {
    const configuredPath = configStore.contentPaths?.homeTiles;
    const fallbackPath = configStore.strapiHomeTilesPath || '/home-tiles?populate=image,product';
    return String(configuredPath || fallbackPath).trim();
});

const strapiTilesUrl = computed(() => {
    const path = contentTilesPath.value;

    if (!path) return '';
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    if (!contentBaseUrl.value) return '';

    return `${contentBaseUrl.value}${path.startsWith('/') ? '' : '/'}${path}`;
});

function mapImageUrl(imageValue) {
    const strapiImage = imageValue?.data?.attributes || imageValue?.data || imageValue?.attributes || imageValue;
    const imageUrl = strapiImage?.url || '';

    if (!imageUrl) return '';
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) return imageUrl;
    if (!contentBaseUrl.value) return imageUrl;

    return `${contentBaseUrl.value}${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
}

function normalizeTile(rawItem, index) {
    const source = rawItem?.attributes ? { id: rawItem.id, ...rawItem.attributes } : rawItem;
    const product = source?.product?.data?.attributes || source?.product || {};

    return {
        id: source?.id || `tile-${index}`,
        title: source?.title || '',
        subtitle: source?.subtitle || '',
        description: source?.description || '',
        imageUrl: mapImageUrl(source?.image),
        pid: source?.pid || source?.productPid || product?.pid || '',
        pgrId: source?.pgrId || source?.productGroupId || product?.pgrId || '',
        stepByStep: source?.stepByStep,
        autoSelect: source?.autoSelect,
        layout: source?.layout,
        bcView: source?.bcView || false,
        ctaText: source?.ctaText || source?.linkText || 'Jetzt konfigurieren'
    };
}

function hasSelectorTarget(tile) {
    return Boolean(tile?.pid || tile?.pgrId);
}

function openProductSelector(tile) {
    if (!hasSelectorTarget(tile)) return;

    const modalPayload = {
        bcView: true
    };

    if (tile.pid) {
        modalPayload.useProd = {
            pid: tile.pid,
            pgrId: tile.pgrId || undefined
        };
    } else {
        modalPayload.usePgrId = tile.pgrId;
    }

    if (typeof tile.stepByStep !== 'undefined') modalPayload.stepByStep = tile.stepByStep;
    if (typeof tile.autoSelect !== 'undefined') modalPayload.autoSelect = tile.autoSelect;
    if (typeof tile.layout !== 'undefined') modalPayload.layout = tile.layout;
    if (typeof tile.bcView !== 'undefined') modalPayload.bcView = tile.bcView;

    psModal(modalPayload);
}

async function loadTiles() {
    if (!strapiTilesUrl.value) {
        tiles.value = [];
        return;
    }

    loadingTiles.value = true;
    tileLoadError.value = '';

    try {
        const response = await fetch(strapiTilesUrl.value, {
            headers: {
                Accept: 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Strapi request failed (${response.status})`);
        }

        const data = await response.json();
        const tileList = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];

        tiles.value = tileList.map(normalizeTile);
    } catch (error) {
        tileLoadError.value = '';
        console.warn('[home] failed to load Strapi tiles', error);
    } finally {
        loadingTiles.value = false;
    }
}

onMounted(() => {
    loadTiles();
});

</script>

<template>
    <!-- <div class="uk-card uk-card-body uk-align-center" v-if="user">
        <h3>{{ $t('home.greeting', { name: user.firstname }) }}</h3>
        <p>{{ $t("home.welcome")  }} {{ configStore.service }}</p>
    </div> -->
    <div class="uk-section uk-section-small">
        <div class="uk-container">
            <div class="home-upload-action">
                <UploadButton button-label="Dateien hochladen"></UploadButton>
            </div>

             <div v-if="loadingTiles" class="uk-text-center">Kacheln werden geladen ...</div>
            <div v-else-if="tileLoadError" class="uk-alert-danger" uk-alert>{{ tileLoadError }}</div>
            <div v-else-if="tiles.length === 0" class="uk-text-center"></div>

            <div v-else class="uk-grid-small uk-child-width-1-1 uk-child-width-1-2@s uk-child-width-1-3@m uk-flex-center" uk-grid>
                <div v-for="tile in tiles" :key="tile.id">
                    <article class="uk-card uk-card-default uk-card-small tile-card">
                        <div v-if="tile.imageUrl" class="uk-card-media-top tile-image-wrap">
                            <img :src="tile.imageUrl" :alt="tile.title || 'Produktkachel'" class="tile-image" loading="lazy">
                        </div>

                        <div class="uk-card-body">
                            <p v-if="tile.subtitle" class="tile-subtitle">{{ tile.subtitle }}</p>
                            <h3 class="uk-card-title tile-title">{{ tile.title }}</h3>
                            <p v-if="tile.description">{{ tile.description }}</p>

                            <button
                                class="uk-button uk-button-primary uk-button-small"
                                :disabled="!hasSelectorTarget(tile)"
                                @click="openProductSelector(tile)"
                            >
                                {{ tile.ctaText }}
                            </button>
                        </div>
                    </article>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.tile-card {
    height: 100%;
}

.home-upload-action {
    display: flex;
    justify-content: center;
    margin-bottom: 18px;
}

.tile-image-wrap {
    max-height: 220px;
    overflow: hidden;
}

.tile-image {
    width: 100%;
    height: 220px;
    object-fit: cover;
}

.tile-subtitle {
    margin-bottom: 4px;
    font-size: 0.85rem;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #666;
}

.tile-title {
    margin-top: 0;
}
</style>
